import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Tabs,
  Tab,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Badge,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';

import {
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  Grade as GradeIcon,
  Schedule as ScheduleIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Message as MessageIcon,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { fetchJson } from '../../../utils/fetchJson';
import { formatBadgeCount, UNREAD_BADGE } from '../../../utils/badgeCount.generated';
import PerformancePanel from '../../../components/Parent/PerformancePanel';
import AnalyticsPanel from '../../../components/Parent/AnalyticsPanel';
import ChildPicker from '../../../components/Parent/ChildPicker';

export default function ParentDashboardPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [children, setChildren] = useState([]);

  const [selectedChild, setSelectedChild] = useState('');
  const [childData, setChildData] = useState({});
  const [academicsData, setAcademicsData] = useState({});
  const [feesData, setFeesData] = useState({});
  const [attendanceRate, setAttendanceRate] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [analytics, setAnalytics] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    loadParentData();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadChildData();
      setTab('overview');
    }
  }, [selectedChild]);

  const loadParentData = async () => {
    try {
      setLoading(true);
      setError(null);

      const childrenRes = await fetchJson('/api/portal/parent/children');
      const childrenData = Array.isArray(childrenRes?.children) ? childrenRes.children : [];
      setChildren(childrenData);
      setNotifications([]);
      if (childrenData.length > 0) {
        setSelectedChild(childrenData[0].studentId || childrenData[0].id);
      } else {
        setSelectedChild('');
        setChildData({});
      }

    } catch (err) {
      console.error('Failed to load parent data:', err);
      setError(t('parentDashboard.errors.loadDashboardData'));
    } finally {
      setLoading(false);
    }
  };

  const loadChildData = async () => {
    if (!selectedChild) return;

    try {
      const dashRes = await fetchJson(`/api/portal/parent/child/${encodeURIComponent(selectedChild)}/dashboard`);
      setChildData(dashRes || {});

      try {
        const acadRes = await fetchJson(`/api/portal/parent/child/${encodeURIComponent(selectedChild)}/academics`);
        setAcademicsData(acadRes || {});
      } catch (e) {
        setAcademicsData({});
      }

      try {
        const feesRes = await fetchJson(`/api/portal/parent/child/${encodeURIComponent(selectedChild)}/fees`);
        setFeesData(feesRes || {});
      } catch (e) {
        setFeesData({});
      }

      try {
        const attRes = await fetchJson(`/api/portal/parent/child/${encodeURIComponent(selectedChild)}/attendance`);
        const records = Array.isArray(attRes?.records) ? attRes.records : [];
        const recent = records.slice(0, 60);
        const considered = recent.filter(r => {
          const s = String(r?.status || '').toLowerCase();
          return s === 'present' || s === 'absent' || s === 'late';
        });
        const attended = considered.filter(r => {
          const s = String(r?.status || '').toLowerCase();
          return s === 'present' || s === 'late';
        }).length;
        const total = considered.length;
        setAttendanceRate(total ? Math.round((attended / total) * 1000) / 10 : null);
      } catch (e) {
        setAttendanceRate(null);
      }

      setAnalytics({});

    } catch (err) {
      console.error('Failed to load child data:', err);
      setError(t('parentDashboard.errors.loadChildData'));
    }
  };

  // Composing a message moved to the Messages page — see the Messages button in
  // Quick actions. The dialog that used to live here is gone along with its
  // state and handlers.

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const selectedChildData = children.find(child => String(child.studentId || child.id) === String(selectedChild));

  if (loading) {
    return (
      <Box p={2}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadParentData}>
            {t('parentDashboard.retry')}
          </Button>
        }>
          {error}

        </Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Card>
        <CardHeader
          title={t('parentDashboard.title')}
          subheader={t('parentDashboard.subheader')}
          action={
            <Box display="flex" gap={1}>
              <Tooltip title={t('parentDashboard.refreshData')}>
                <IconButton onClick={loadParentData}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('parentDashboard.notifications')}>
                <IconButton>
                  <Badge
                    badgeContent={formatBadgeCount(notifications.length)}
                    invisible={!(notifications.length > 0)}
                    sx={{ '& .MuiBadge-badge': { bgcolor: UNREAD_BADGE.bg, color: UNREAD_BADGE.fg, fontWeight: 700 } }}>
                    <NotificationsIcon />

                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          }
        />
        <CardContent>
          {/* Names the child, and offers the switch only when there is a
              second one to switch to. The header and the dropdown used to be
              two separate blocks here saying the same thing twice. */}
          <ChildPicker
            children={children}
            value={selectedChild}
            onChange={setSelectedChild}
            label={t('parentDashboard.selectChild')}
            studentWord={t('parentDashboard.student')}
          />

          {selectedChild && selectedChildData && (
            <>
              {/* Quick Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {childData?.summary?.subjects || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('parentDashboard.quickStats.subjects')}
                      </Typography>

                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">
                        {selectedChildData?.studentNumber || '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('parentDashboard.quickStats.studentNumber')}
                      </Typography>

                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      {(() => {
                        const outstanding = Number(feesData?.summary?.outstanding || 0);
                        const complete = !(outstanding > 0);
                        return (
                          <>
                            <Chip label={complete ? t('parentDashboard.status.complete') : t('parentDashboard.status.stillOwing')} color={complete ? 'success' : 'warning'} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {t('parentDashboard.quickStats.feesStatus')}
                            </Typography>

                          </>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="info.main">
                        {attendanceRate != null ? `${attendanceRate.toFixed(1)}%` : '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('parentDashboard.quickStats.attendanceRate')}
                      </Typography>

                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                  <Tab icon={<DashboardIcon />} label={t('parentDashboard.tabs.overview')} value="overview" />
                  <Tab icon={<GradeIcon />} label={t('parentDashboard.tabs.academicPerformance')} value="performance" />
                  <Tab icon={<SchoolIcon />} label={t('parentDashboard.tabs.feesStatus')} value="fees" />
                  <Tab icon={<TrendingUpIcon />} label={t('parentDashboard.tabs.analytics')} value="analytics" />
                </Tabs>
              </Box>

              {/* Tab Content */}
              {tab === 'overview' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Card variant="outlined">
                      <CardHeader title={t('parentDashboard.academicPerformance')} />

                      <CardContent>
                        <List>
                          {(Array.isArray(academicsData?.marks) ? academicsData.marks : []).slice(0, 6).map((m, index, arr) => {
                            const subject = m.subjectName || m.subjectCode || (m.subjectId != null ? t('parentDashboard.subjectFallbackWithId', { id: m.subjectId }) : t('parentDashboard.subjectFallback'));
                            const seq = m.sequenceNumber != null ? t('parentDashboard.sequenceLabel', { number: m.sequenceNumber }) : (m.evaluationType || '');

                            const score = m.score != null ? Number(m.score) : null;
                            const passMark = Number(academicsData?.grading?.passMark || 10);
                            const failed = score != null && Number.isFinite(passMark) && score < passMark;
                            return (
                              <React.Fragment key={m.id || `${m.subjectId}-${m.createdAt}-${index}`}>
                                <ListItem>
                                  <ListItemAvatar>
                                    <Avatar>
                                      <GradeIcon />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={subject}
                                    secondary={[seq, m.createdAt ? new Date(m.createdAt).toLocaleString() : null].filter(Boolean).join(' • ')}
                                  />
                                  <Chip
                                    label={score != null ? score.toFixed(2) : '—'}
                                    color={failed ? 'error' : 'primary'}
                                    size="small"
                                  />
                                </ListItem>
                                {index < arr.length - 1 && <Divider />}
                              </React.Fragment>
                            );
                          })}
                          {(Array.isArray(academicsData?.marks) ? academicsData.marks : []).length === 0 && (
                            <ListItem>
                              <ListItemText primary={t('parentDashboard.noAcademicRecordsYet')} />
                            </ListItem>
                          )}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardHeader title={t('parentDashboard.quickActions')} />
                      <CardContent>
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Button variant="outlined" startIcon={<SchoolIcon />} fullWidth onClick={() => navigate('/parent/children-academics')}>
                            {t('parentDashboard.actions.viewAcademicPerformance')}
                          </Button>
                          <Button variant="outlined" startIcon={<CalendarIcon />} fullWidth onClick={() => navigate('/parent/attendance')}>
                            {t('parentDashboard.actions.viewAttendance')}
                          </Button>
                          <Button variant="outlined" startIcon={<ScheduleIcon />} fullWidth onClick={() => navigate('/parent/fees-payments')}>
                            {t('parentDashboard.actions.feesPayments')}
                          </Button>
                          {/* Goes to the Messages page rather than opening a
                              compose dialog. The dialog dropped the user into
                              writing with no sight of the conversation they were
                              replying to, and left them nowhere after sending —
                              the composer belongs alongside the thread. */}
                          <Button variant="outlined" startIcon={<MessageIcon />} fullWidth onClick={() => navigate('/communication/messages')}>
                            {t('parentDashboard.actions.messages', 'Messages')}
                          </Button>
                        </Box>
                      </CardContent>

                    </Card>
                  </Grid>
                </Grid>
              )}

              {tab === 'performance' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>{t('parentDashboard.academicPerformance')}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        {/* THE SAME PANEL THE ACADEMICS PAGE DRAWS. This tab had
                            its own copy of the marks table — a different table
                            for the same marks, on two pages of the same portal.
                            One sequence at a time, coefficient-weighted averages,
                            and no annual average until the year is finished. */}
                        <PerformancePanel
                          subjects={Array.isArray(academicsData?.subjects) ? academicsData.subjects : []}
                          marks={Array.isArray(academicsData?.marks) ? academicsData.marks : []}
                          grading={academicsData?.grading}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {tab === 'fees' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>{t('parentDashboard.feesStatus')}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">{t('parentDashboard.summary.totalExpected')}</Typography>
                        <Typography variant="h6">{Number(feesData?.summary?.totalExpected || 0).toLocaleString()}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('parentDashboard.summary.totalPaid')}</Typography>
                        <Typography variant="h6">{Number(feesData?.summary?.totalPaid || 0).toLocaleString()}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('parentDashboard.summary.outstanding')}</Typography>
                        <Typography variant="h6">{Number(feesData?.summary?.outstanding || 0).toLocaleString()}</Typography>

                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        {(() => {
                          const outstanding = Number(feesData?.summary?.outstanding || 0);
                          const complete = !(outstanding > 0);
                          return (
                            <>
                              <Typography variant="body2" color="text.secondary" gutterBottom>{t('parentDashboard.status.label')}</Typography>
                              <Chip label={complete ? t('parentDashboard.status.complete') : t('parentDashboard.status.stillOwing')} color={complete ? 'success' : 'warning'} />
                              <Box sx={{ mt: 2 }}>
                                <Button variant="contained" onClick={() => navigate('/parent/fees-payments')}>{t('parentDashboard.goToFeesPayments')}</Button>
                              </Box>

                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {tab === 'analytics' && (
                <AnalyticsPanel
                  subjects={Array.isArray(academicsData?.subjects) ? academicsData.subjects : []}
                  marks={Array.isArray(academicsData?.marks) ? academicsData.marks : []}
                  grading={academicsData?.grading}
                  attendanceRate={attendanceRate}
                  fees={feesData?.summary}
                />
              )}
            </>
          )}

          {!selectedChild && (
            <Alert severity="info">
              {t('parentDashboard.selectChildPrompt')}
            </Alert>
          )}

        </CardContent>
      </Card>
    </Box>
  );
}